import { lazy, Suspense } from "react";
import { useDesignVersion } from "@/design-version/context";
const DesignSwitcher = lazy(() =>
  import("@/revamped/switcher").then((m) => ({ default: m.DesignSwitcher }))
);

import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { MissingFieldsProvider } from "@/lib/missing-fields";
import { AppErrorPage, NotFoundPage } from "@/components/app/brand-fallback";
import { useSystemThemeSync } from "@/lib/prefs";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: AppErrorPage,
  notFoundComponent: NotFoundPage,
});

function RootLayout() {
  useSystemThemeSync();
  const revamped = useDesignVersion() === "revamped";
  return (
    <>
      {revamped && (
        <Suspense fallback={null}>
          <DesignSwitcher />
        </Suspense>
      )}
      {/* The missing-field notice for every screen (lib/missing-fields); a form may nest its own. */}
      <MissingFieldsProvider>
        <Outlet />
      </MissingFieldsProvider>
      {/* Token-themed, not sonner's richColors: toasts follow the app palette and dark mode. */}
      <Toaster
        position="top-right"
        toastOptions={{
          classNames: {
            toast:
              "!rounded-xl !border-border !bg-surface-raised !text-ink !shadow-pop",
            description: "!text-ink-muted",
            closeButton:
              "!border-border !bg-surface-raised !text-ink-muted hover:!text-ink",
            success: "[&_[data-icon]]:!text-success",
            error: "!border-danger-border [&_[data-icon]]:!text-danger",
            warning: "!border-warning-border [&_[data-icon]]:!text-warning",
            info: "[&_[data-icon]]:!text-brand-600",
          },
        }}
      />
    </>
  );
}
